import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft, Check, ChevronDown, X, Minus, Plus } from "lucide-react";
import productPlaceholder from "../../assets/product-placeholder.svg";
import { Slider } from "../ui/slider";
import {
  buildOptionValueMap,
  calculateOptionAddonTotal,
  cloneOptionValues,
  formatAddonLabel,
  formatCurrency,
  hexToRgba,
  isConfigurableProduct,
  isOptionValueMissing,
  getProductImage,
  resolveDefaultVariableSelection,
  resolveVariableSelectionMeta,
  normalizeExtraInfoBlocks,
  normalizeProductOptions,
  resolveMaxQuantity,
  resolveProductPriceInfo,
  resolveProductStartingPrice,
  resolveVariantPrice,
  roundCurrency,
} from "../../lib/productUtils";

const productImageFallback = productPlaceholder;

function Lightbox({ src, onClose }) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    document.body.dataset.lightboxOpen = "true";
    const handleCloseRequest = () => onCloseRef.current();
    document.addEventListener("lightbox:close", handleCloseRequest);
    return () => {
      delete document.body.dataset.lightboxOpen;
      document.removeEventListener("lightbox:close", handleCloseRequest);
    };
  }, []);

  return createPortal(
    <div
      data-lightbox="true"
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      style={{ cursor: "pointer" }}
      onPointerDown={(e) => { e.stopPropagation(); onClose(); }}
    >
      <div className="absolute inset-0 bg-black/85 backdrop-blur-md" />
      <img
        src={src}
        alt="Anteprima"
        className="relative max-h-[90vh] max-w-[90vw] rounded-lg object-contain shadow-2xl"
        onPointerDown={(e) => e.stopPropagation()}
      />
      <button
        type="button"
        onPointerDown={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur-sm"
        aria-label="Chiudi anteprima"
      >
        <X className="h-5 w-5" />
      </button>
    </div>,
    document.body
  );
}

function GalleryCarousel({ block, primaryColor }) {
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const total = block.images.length;

  return (
    <div className="overflow-hidden rounded-xl">
      {block.title && (
        <p className="pb-2 pt-3 text-sm font-semibold text-[#0f172a]">{block.title}</p>
      )}
      {total === 1 ? (
        <button
          type="button"
          onClick={() => setLightboxSrc(block.images[0].url)}
          className="w-full overflow-hidden"
          aria-label="Apri immagine"
        >
          <img
            src={block.images[0].url}
            alt={block.title || "Immagine"}
            className="h-auto w-full object-cover"
          />
        </button>
      ) : (
        <div className="no-scrollbar flex gap-2 overflow-x-auto px-3 pb-3 pt-2" style={{paddingLeft: (total > 1 ? 0 : undefined)}}>
          {block.images.map((img, i) => (
            <button
              key={img._key || i}
              type="button"
              onClick={() => setLightboxSrc(img.url)}
              className="shrink-0 overflow-hidden rounded-xl border"
              style={{ width: 300, height: 300, borderColor: primaryColor }}
              aria-label={`Immagine ${i + 1}`}
            >
              <img
                src={img.url}
                alt={`Immagine ${i + 1}`}
                className="h-full w-full object-cover" 
              />
            </button>
          ))}
        </div>
      )}
      {lightboxSrc && <Lightbox src={lightboxSrc} onClose={() => setLightboxSrc(null)} />}
    </div>
  );
}

function FaqAccordion({ block }) {
  const [openKey, setOpenKey] = useState(null);
  return (
    <div className="overflow-hidden rounded-xl border border-[#e2e8f0] bg-white">
      {block.title && (
        <p className="border-b border-[#e2e8f0] px-3 pb-2 pt-3 text-sm font-semibold text-[#0f172a]">
          {block.title}
        </p>
      )}
      <div className="divide-y divide-[#e2e8f0]">
        {block.items.map((item) => {
          const open = openKey === item._key;
          return (
            <div key={item._key}>
              <button
                type="button"
                onClick={() => setOpenKey(open ? null : item._key)}
                className="flex w-full items-center justify-between gap-3 px-3 py-3 text-left"
              >
                <span className="text-sm font-medium text-[#0f172a]">{item.question}</span>
                <ChevronDown
                  className={`h-4 w-4 shrink-0 text-[#94a3b8] transition-transform duration-200 ${
                    open ? "rotate-180" : ""
                  }`}
                />
              </button>
              {open && (
                <p className="px-3 pb-3 text-sm leading-5 text-[#475569]">{item.answer}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * DrawerProductDetail — shown when:
 *  - Simple product: row tap → product info + inline qty control
 *  - Configurable product NOT in cart: row tap / "Scegli" → full configure view
 *  - Configurable product: "Aggiungi" from inCart → fresh configure
 *  - Any product: "Modifica" from inCart or cart → configure with pre-loaded values
 *
 * Design follows ProductDetail.jsx exactly:
 *  - Variants → radiobox rows
 *  - Options → cards with badge "Obbligatoria", proper input/choice/checkbox
 *  - Extra info → text / faq / table blocks
 *  - CTA footer uses `accentColor` (--tenant-accent)
 */

/** Compact name + brand + price row used in sticky headers for every image mode. */
function StickyInfoRow({ product, accentColor }) {
  const { current, original, hasDiscount } = resolveProductPriceInfo(product);

  return (
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        <h2 className="text-[18px] font-semibold leading-[1.35] text-[#020617]">{product?.name || "Prodotto"}</h2>
        {product?.brandId?.name && (
          <p className="mt-0.5 text-xs font-semibold uppercase tracking-[0.6px]" style={{ color: accentColor }}>
            {product.brandId.name}
          </p>
        )}
      </div>
      {current > 0 && (
        <div className="shrink-0 text-right">
          <div className="flex items-baseline justify-end gap-1.5">
            {hasDiscount && !product?.hasVariants && (
              <span className="text-xs text-[#94a3b8] line-through">{formatCurrency(original)}</span>
            )}
            <span className="text-sm font-semibold text-[#020617]">
              {product?.hasVariants ? `da ${formatCurrency(current)}` : formatCurrency(current)}
            </span>
          </div>
          {!product?.hasVariants && product?.pricingType?.label && (
            <p className="text-[11px] text-[#94a3b8]">{`/ ${product.pricingType.label}`}</p>
          )}
        </div>
      )}
    </div>
  );
}

export function DrawerProductDetail({
  product,
  primaryColor,
  accentColor,
  editingLine,        // CartLine | null — pre-loads values when editing
  productQtyInCart,   // total qty of this product across all cart lines
  inventoryManagement, // Boolean — from tenant config
  onClose,
  onSaveConfigure,    // (variantId, optionValues, quantity, editingLineId, variableSelection) => void
  onAddSimple,        // (product) => void  — kept for non-drawer inline catalog rows
  onDecreaseSimple,   // (product) => void  — kept for non-drawer inline catalog rows
  onSetSimpleQty,     // (product, qty) => void — drawer footer for simple products
}) {
  const configurable = isConfigurableProduct(product);
  const productOptions = useMemo(() => normalizeProductOptions(product?.options), [product?.options]);
  const extraInfoBlocks = useMemo(() => normalizeExtraInfoBlocks(product?.extraInfo), [product?.extraInfo]);

  // ─── internal state ───────────────────────────────────────────────────────
  const [variantId, setVariantId] = useState("");
  const [optionValues, setOptionValues] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [variableSelectionValue, setVariableSelectionValue] = useState(null);
  const [itemNotes, setItemNotes] = useState("");
  const [descExpanded, setDescExpanded] = useState(false);
  const [previewLightboxOpen, setPreviewLightboxOpen] = useState(false);

  const variablePricingMeta = useMemo(() => resolveVariableSelectionMeta(product), [product]);
  const isVariablePricing = Boolean(variablePricingMeta.enabled);

  // Reset whenever the product or the line being edited changes
  useEffect(() => {
    if (!product) return;
    const opts = normalizeProductOptions(product?.options);
    const defaults = buildOptionValueMap(opts);
    setOptionValues(
      editingLine?.optionValues
        ? { ...defaults, ...cloneOptionValues(editingLine.optionValues) }
        : defaults
    );
    const firstVariantId =
      product?.hasVariants && Array.isArray(product?.variants) && product.variants.length
        ? String(product.variants[0]._id)
        : "";
    setVariantId(editingLine?.variantId || firstVariantId);
    const isConfigurableProduct_ = isConfigurableProduct(product);
    setQuantity(
      !isConfigurableProduct_ && productQtyInCart > 0
        ? productQtyInCart
        : Math.max(1, Number(editingLine?.quantity) || 1)
    );
    const defaultSelection = resolveDefaultVariableSelection(product);
    const editedSelectionValue = Number(editingLine?.variableSelection?.value);
    if (defaultSelection && Number.isFinite(editedSelectionValue) && editedSelectionValue > 0) {
      setVariableSelectionValue(editedSelectionValue);
    } else if (defaultSelection) {
      setVariableSelectionValue(defaultSelection.value);
    } else {
      setVariableSelectionValue(null);
    }
    setItemNotes(editingLine?.notes || "");
    setDescExpanded(false);
  }, [product?._id, editingLine?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── derived values ───────────────────────────────────────────────────────
  const selectedVariant = useMemo(() => {
    if (!product?.hasVariants || !Array.isArray(product?.variants)) return null;
    return product.variants.find((v) => String(v._id) === String(variantId)) || null;
  }, [product, variantId]);

  const unitPrice = useMemo(() => {
    if (!product) return 0;
    const rawBase = selectedVariant ? resolveVariantPrice(selectedVariant) : resolveProductStartingPrice(product);
    if (!isVariablePricing) return rawBase;

    const selectedValue = Number(variableSelectionValue);
    if (!Number.isFinite(selectedValue) || selectedValue <= 0) return 0;
    const conversionFactor = Number(variablePricingMeta.conversionFactor) > 0
      ? Number(variablePricingMeta.conversionFactor)
      : 1;
    return roundCurrency(rawBase * selectedValue * conversionFactor);
  }, [isVariablePricing, product, selectedVariant, variablePricingMeta.conversionFactor, variableSelectionValue]);

  const optionsAddonTotal = useMemo(
    () => calculateOptionAddonTotal(productOptions, optionValues),
    [optionValues, productOptions]
  );

  const totalPrice = useMemo(
    () => roundCurrency((unitPrice + optionsAddonTotal) * quantity),
    [optionsAddonTotal, quantity, unitPrice]
  );

  const missingRequiredCount = useMemo(() => {
    return productOptions.reduce((count, option) => {
      return count + (isOptionValueMissing(option, optionValues[option._key]) ? 1 : 0);
    }, 0);
  }, [optionValues, productOptions]);

  const maxQuantity = useMemo(() => resolveMaxQuantity(product, inventoryManagement), [product, inventoryManagement]);
  const soldOut = maxQuantity === 0;

  // When editing, the editing line's qty doesn't count against the limit
  const editingBaseQty = Number(editingLine?.quantity) || 0;
  const otherQtyInCart = Math.max(0, productQtyInCart - editingBaseQty);
  const maxAllowed = Number.isFinite(maxQuantity) ? Math.max(0, maxQuantity - otherQtyInCart) : Number.POSITIVE_INFINITY;
  const canIncrement = !Number.isFinite(maxQuantity) || otherQtyInCart + quantity + 1 <= maxQuantity;
  const exceedsMax = Number.isFinite(maxQuantity) && otherQtyInCart + quantity > maxQuantity;

  const canSubmit =
    configurable &&
    (!product?.hasVariants || Boolean(selectedVariant)) &&
    (!isVariablePricing || (Number.isFinite(Number(variableSelectionValue)) && Number(variableSelectionValue) > 0)) &&
    missingRequiredCount === 0 &&
    !exceedsMax &&
    !soldOut;

  const canAddSimple = !configurable && !soldOut && quantity > 0;

  // ─── style helpers ────────────────────────────────────────────────────────
  const selectedRowStyle = { borderColor: primaryColor, backgroundColor: hexToRgba(primaryColor, 0.08) };
  const selectedDotStyle = { borderColor: primaryColor, backgroundColor: primaryColor };

  const previewImageUrl = getProductImage(product, "");
  const hasImage = Boolean(previewImageUrl);
  const isPreviewMode = (product?.imageDisplayMode || "cover") === "preview";

  // ─── handlers ────────────────────────────────────────────────────────────
  function updateOptionValue(key, value) {
    setOptionValues((prev) => ({ ...prev, [key]: value }));
  }

  function toggleMultipleChoice(optionKey, choiceKey) {
    setOptionValues((prev) => {
      const arr = Array.isArray(prev[optionKey]) ? prev[optionKey] : [];
      const next = arr.includes(choiceKey) ? arr.filter((v) => v !== choiceKey) : [...arr, choiceKey];
      return { ...prev, [optionKey]: next };
    });
  }

  function handleSave() {
    if (!canSubmit) return;
    onSaveConfigure(
      variantId,
      optionValues,
      quantity,
      editingLine?.id || "",
      isVariablePricing
        ? {
            value: Number(variableSelectionValue),
            unit: variablePricingMeta.selectionUnit || "",
          }
        : null,
      itemNotes.trim()
    );
  }

  // ─── rendering ───────────────────────────────────────────────────────────
  return (
    <div className="flex max-h-[85vh] flex-col">

      {/* ── Preview image ───────────────────────────────────────────────────── */}
      {(!hasImage || isPreviewMode) && (
        <div className="shrink-0 px-4 pb-1 pt-3">
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-[#e2e8f0] bg-white text-[#334155]"
            aria-label="Chiudi"
          >
            <ArrowLeft className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* ── Scrollable content ─────────────────────────────────────────────── */}
      <div className={`flex-1 overflow-auto ${configurable ? "pb-[140px]" : "pb-8"}`}>

        {/* Product info ── same structure as ProductDetail.jsx */}
        {hasImage && !isPreviewMode && (
          <section className="relative mb-8">
            <div className="relative h-[350px] overflow-hidden rounded-t-2xl bg-[#e2e8f0]">
              <img
                src={previewImageUrl}
                alt={product?.name || "Prodotto"}
                className="h-full w-full object-cover"
                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = productImageFallback; }}
              />
              <div className="absolute left-3 top-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/95 text-[#334155] shadow-sm"
                  aria-label="Chiudi"
                >
                  <ArrowLeft className="h-5 w-5" />
                </button>
              </div>
            </div>
          </section>
        )}

        {hasImage && !isPreviewMode ? (
          // Cover mode — sticky name + brand + price, description scrolls below
          <>
            <div className="sticky top-0 z-10 -mt-6 rounded-t-[28px] border-b border-[#e2e8f0] bg-white px-5 pb-3 pt-4">
              <StickyInfoRow
                product={product}
                accentColor={accentColor}
              />
            </div>
            {product?.description && (
              <section className="border-b border-[#e2e8f0] px-5 pb-5 pt-3">
                <p className={`text-sm leading-5 text-[#64748b] ${descExpanded ? "" : "line-clamp-3"}`}>
                  {product.description}
                </p>
                <button
                  type="button"
                  onClick={() => setDescExpanded((v) => !v)}
                  className="mt-1 text-xs font-medium"
                  style={{ color: "var(--tenant-primary)" }}
                >
                  {descExpanded ? "Nascondi" : "Leggi tutto"}
                </button>
              </section>
            )}
          </>
        ) : hasImage && isPreviewMode ? (
          // Preview mode — sticky thumbnail + name + brand + price, description scrolls below
          <>
            <div className="sticky top-0 z-10 border-b border-[#e2e8f0] bg-white px-5 pb-3 pt-2">
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setPreviewLightboxOpen(true)}
                  className="shrink-0 overflow-hidden rounded-xl border border-[#e2e8f0] shadow-sm"
                  style={{ width: 56, height: 56 }}
                  aria-label="Apri immagine prodotto"
                >
                  <img
                    src={previewImageUrl}
                    alt={product?.name || "Prodotto"}
                    className="h-full w-full object-cover"
                    onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = productImageFallback; }}
                  />
                </button>
                <div className="min-w-0 flex-1">
                  <StickyInfoRow
                    product={product}
                    accentColor={accentColor}
                  />
                </div>
              </div>
            </div>
            {product?.description && (
              <section className="border-b border-[#e2e8f0] px-5 pb-5 pt-3">
                <p className={`text-sm leading-5 text-[#64748b] ${descExpanded ? "" : "line-clamp-3"}`}>
                  {product.description}
                </p>
                <button
                  type="button"
                  onClick={() => setDescExpanded((v) => !v)}
                  className="mt-1 text-xs font-medium"
                  style={{ color: "var(--tenant-primary)" }}
                >
                  {descExpanded ? "Nascondi" : "Leggi tutto"}
                </button>
              </section>
            )}
            {previewLightboxOpen && <Lightbox src={previewImageUrl} onClose={() => setPreviewLightboxOpen(false)} />}
          </>
        ) : (
          // No image — name + brand + description + price in a single section
          <section className="border-b border-[#e2e8f0] px-5 pb-6 pt-2">
            <h2 className="text-[20px] font-semibold leading-6 text-[#020617]">{product?.name || "Prodotto"}</h2>
            {product?.brandId?.name && (
              <p className="mt-2 text-xs font-semibold uppercase tracking-[0.6px]" style={{ color: accentColor }}>
                {product.brandId.name}
              </p>
            )}
            {product?.description ? (
              <div className="mt-2">
                <p className={`text-sm leading-5 text-[#64748b] ${descExpanded ? "" : "line-clamp-3"}`}>
                  {product.description}
                </p>
                <button
                  type="button"
                  onClick={() => setDescExpanded((v) => !v)}
                  className="mt-1 text-xs font-medium"
                  style={{ color: "var(--tenant-primary)" }}
                >
                  {descExpanded ? "Nascondi" : "Leggi tutto"}
                </button>
              </div>
            ) : null}
            {resolveProductStartingPrice(product) > 0 ? (
              <div className="mt-4 flex items-start justify-between gap-3 text-sm">
                <span className="text-[#64748b]">
                  {product?.hasVariants
                    ? "Prezzo (a partire da):"
                    : `Prezzo${product?.pricingType?.label ? ` / ${product.pricingType.label}` : ""}:`}
                </span>
                <div className="text-right">
                  {!product?.hasVariants && Number(product?.price?.effectiveDiscounted) > 0 && Number(product?.price?.effectiveDiscounted) < Number(product?.price?.effectiveBase || product?.price?.base) && (
                    <p className="text-xs text-[#94a3b8] line-through">
                      {formatCurrency(Number(product?.price?.effectiveBase || product?.price?.base) || 0)}
                    </p>
                  )}
                  <p className="font-semibold text-[#020617]">{formatCurrency(resolveProductStartingPrice(product))}</p>
                </div>
              </div>
            ) : null}
          </section>
        )}

        {/* ── Variants section ── ProductDetail.jsx radiobox style ─────────── */}
        {configurable && product?.hasVariants && Array.isArray(product?.variants) && product.variants.length ? (
          <section className="border-y border-[#e2e8f0] px-5 py-6">
            <p className="text-xs font-semibold uppercase tracking-[0.6px]" style={{ color: primaryColor }}>
              Varianti
            </p>
            <p className="mt-1 text-xs text-[#64748b]">Seleziona un formato prodotto.</p>
            <div className="mt-5 space-y-3">
              {product.variants.map((variant) => {
                const vId = String(variant._id);
                const checked = vId === String(variantId);
                const vPrice = resolveVariantPrice(variant);
                const originalPrice = Number(variant?.effectiveBase ?? variant?.price) || 0;
                const hasDiscount = vPrice > 0 && vPrice < originalPrice;
                return (
                  <button
                    key={vId}
                    type="button"
                    onClick={() => setVariantId(vId)}
                    className="flex w-full items-center gap-2 rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-left"
                    style={checked ? selectedRowStyle : undefined}
                  >
                    <span
                      className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-[#94a3b8]"
                      style={checked ? { borderColor: primaryColor } : undefined}
                    >
                      {checked && <span className="h-2 w-2 rounded-full" style={selectedDotStyle} />}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-[#334155]">{variant?.name || "Variante"}</p>
                      {variant?.description && (
                        <p className="truncate text-xs text-[#64748b]">{variant.description}</p>
                      )}
                    </div>
                    <div className="text-right">
                      {hasDiscount && (
                        <p className="text-xs text-[#94a3b8] line-through">{formatCurrency(originalPrice)}</p>
                      )}
                      {vPrice > 0 ? (
                        <p className="text-sm font-semibold text-[#020617]">{formatCurrency(vPrice)}</p>
                      ) : null}
                      {(() => {
                        const vLabel = variant?.pricingType?.label || product?.pricingType?.label || null;
                        return vLabel ? (
                          <span className="block text-[10px] text-[#94a3b8]">{vLabel}</span>
                        ) : null;
                      })()}
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        ) : null}

        {/* ── Options section ── ProductDetail.jsx card + input style ──────── */}
        {configurable && productOptions.length ? (
          <section className="border-b border-[#e2e8f0] px-5 py-6">
            <p className="text-xs font-semibold uppercase tracking-[0.6px]" style={{ color: primaryColor }}>
              Configurazioni
            </p>
            <p className="mt-1 text-xs text-[#64748b]">Completa le opzioni richieste per questo prodotto.</p>
            <div className="mt-4 space-y-4">
              {productOptions.map((option) => {
                const value = optionValues[option._key];
                const missing = isOptionValueMissing(option, value);
                return (
                  <div
                    key={option._key}
                    className="rounded-xl border border-[#e2e8f0] bg-white p-3"
                    style={missing ? { borderColor: hexToRgba(primaryColor, 0.45) } : undefined}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-sm font-semibold text-[#0f172a]">
                          {option.label}{option.required ? " *" : ""}
                        </p>
                        {option.description && (
                          <p className="mt-0.5 text-xs text-[#64748b]">{option.description}</p>
                        )}
                      </div>
                      {option.required && (
                        <span className="rounded-full bg-[#f1f5f9] px-2 py-0.5 text-[11px] font-medium text-[#475569]">
                          Obbligatoria
                        </span>
                      )}
                    </div>

                    {/* text / number / date */}
                    {(option.type === "text" || option.type === "number" || option.type === "date") && (
                      <input
                        type={option.type === "date" ? "date" : option.type === "number" ? "number" : "text"}
                        value={String(value || "")}
                        onChange={(e) => updateOptionValue(option._key, e.target.value)}
                        placeholder={
                          option.type === "text"
                            ? "Scrivi qui..."
                            : option.type === "number"
                            ? "Inserisci un numero"
                            : undefined
                        }
                        className="mt-3 h-10 w-full rounded-lg border border-[#e2e8f0] px-3 text-sm outline-none focus:border-[var(--tenant-primary)]"
                      />
                    )}

                    {/* single_choice — radiobox rows like ProductDetail.jsx */}
                    {option.type === "single_choice" && (
                      <div className="mt-3 space-y-2">
                        {option.choices.map((choice) => {
                          const checked = String(value || "") === choice._key;
                          return (
                            <button
                              key={choice._key}
                              type="button"
                              onClick={() => updateOptionValue(option._key, choice._key)}
                              className="flex w-full items-center gap-3 rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-left"
                              style={checked ? selectedRowStyle : undefined}
                            >
                              <span
                                className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border border-[#94a3b8]"
                                style={checked ? { borderColor: primaryColor } : undefined}
                              >
                                {checked && <span className="h-2 w-2 rounded-full" style={selectedDotStyle} />}
                              </span>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm text-[#0f172a]">{choice.label}</p>
                                {choice.description && (
                                  <p className="truncate text-xs text-[#64748b]">{choice.description}</p>
                                )}
                              </div>
                              <span className="text-xs font-medium text-[#334155]">
                                {formatAddonLabel(choice.priceAddon)}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* multiple_choice — checkbox rows like ProductDetail.jsx */}
                    {option.type === "multiple_choice" && (
                      <div className="mt-3 space-y-2">
                        {option.choices.map((choice) => {
                          const selectedKeys = Array.isArray(value) ? value : [];
                          const checked = selectedKeys.includes(choice._key);
                          return (
                            <button
                              key={choice._key}
                              type="button"
                              onClick={() => toggleMultipleChoice(option._key, choice._key)}
                              className="flex w-full items-center gap-3 rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-left"
                              style={checked ? selectedRowStyle : undefined}
                            >
                              <span
                                className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded border border-[#94a3b8]"
                                style={checked ? { borderColor: primaryColor, backgroundColor: primaryColor } : undefined}
                              >
                                {checked && <Check className="h-3 w-3 text-white" />}
                              </span>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm text-[#0f172a]">{choice.label}</p>
                                {choice.description && (
                                  <p className="truncate text-xs text-[#64748b]">{choice.description}</p>
                                )}
                              </div>
                              <span className="text-xs font-medium text-[#334155]">
                                {formatAddonLabel(choice.priceAddon)}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ) : null}

        {isVariablePricing && (
          <section className="border-b border-[#e2e8f0] px-5 py-6">
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs font-semibold uppercase tracking-[0.6px]" style={{ color: primaryColor }}>
                Quantità variabile
              </p>
              <p className="text-sm font-semibold text-[#0f172a]">
                {Number(variableSelectionValue) || 0} {variablePricingMeta.selectionUnit || "unit"}
              </p>
            </div>
            <Slider
              className="mt-3"
              style={{ "--slider-accent": accentColor }}
              min={Number.isFinite(variablePricingMeta.minSelection) ? variablePricingMeta.minSelection : 1}
              max={Number.isFinite(variablePricingMeta.maxSelection) ? variablePricingMeta.maxSelection : 1}
              step={Number.isFinite(variablePricingMeta.stepSelection) ? variablePricingMeta.stepSelection : 1}
              value={[Number(variableSelectionValue) || 0]}
              onValueChange={(values) => setVariableSelectionValue(Number(values?.[0]) || 0)}
            />
            <div className="mt-2 flex items-center justify-between text-[11px] text-[#64748b]">
              <span>
                Min: {Number.isFinite(variablePricingMeta.minSelection) ? variablePricingMeta.minSelection : "-"}
              </span>
              {/*
              <span>
                Step: {Number.isFinite(variablePricingMeta.stepSelection) ? variablePricingMeta.stepSelection : "-"}
              </span>
              */}
              <span>
                Max: {Number.isFinite(variablePricingMeta.maxSelection) ? variablePricingMeta.maxSelection : "-"}
              </span>
            </div>
          </section>
        )}

        {/* ── Quantity section ── ProductDetail.jsx stepper style ───────────── */}
        <section className="border-b border-[#e2e8f0] px-5 py-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.6px]" style={{ color: primaryColor }}>
                {configurable ? "Quantità" : "Quantità nel carrello"}
              </p>
              <p className="mt-1 text-xs text-[#64748b]">
                {soldOut
                  ? "Disponibilità: esaurito"
                  : inventoryManagement && Number.isFinite(maxQuantity) && maxQuantity > 0
                  ? `Disponibilità: ${maxQuantity}`
                  : null}
              </p>
            </div>

            {configurable ? (
              /* configurable — stepper for the new/edit line quantity */
              <div className="inline-flex h-9 items-center gap-2 rounded-lg border border-[#e2e8f0] bg-white px-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                <button
                  type="button"
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  disabled={quantity <= 1}
                  className="inline-flex h-5 w-5 items-center justify-center rounded text-[#334155] disabled:opacity-40"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span className="min-w-5 text-center text-sm font-semibold text-[#020617]">{quantity}</span>
                <button
                  type="button"
                  onClick={() => setQuantity((q) => q + 1)}
                  disabled={!canIncrement}
                  className="inline-flex h-5 w-5 items-center justify-center rounded text-[#334155] disabled:opacity-40"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            ) : soldOut ? (
              <span className="rounded-full bg-[#f8d7da] px-3 py-1 text-xs font-semibold text-[#b42318]">
                Esaurito
              </span>
            ) : (
              /* simple — local stepper, cart updated via footer button */
              <div className="inline-flex h-9 items-center gap-2 rounded-lg border border-[#e2e8f0] bg-white px-3 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                <button
                  type="button"
                  onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                  disabled={quantity <= 1}
                  className="inline-flex h-5 w-5 items-center justify-center rounded text-[#334155] disabled:opacity-40"
                >
                  <Minus className="h-4 w-4" />
                </button>
                <span className="min-w-5 text-center text-sm font-semibold text-[#020617]">{quantity}</span>
                <button
                  type="button"
                  onClick={() => setQuantity((q) => q + 1)}
                  disabled={!canIncrement}
                  className="inline-flex h-5 w-5 items-center justify-center rounded text-[#334155] disabled:opacity-40"
                >
                  <Plus className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>

          {/* Availability hint for configurable — only when inventory management is on */}
          {inventoryManagement && configurable && Number.isFinite(maxQuantity) && maxAllowed > 0 && (
            <p className="mt-2 text-xs text-[#64748b]">Disponibili ancora: {Math.max(0, maxAllowed)}</p>
          )}
        </section>

        {/* ── Item notes section ── standard field like quantity ───────────────── */}
        {configurable && (
          <section className="border-b border-[#e2e8f0] px-5 py-6">
            <label className="block text-xs font-semibold uppercase tracking-[0.6px]" style={{ color: primaryColor }}>
              Note (opzionali)
            </label>
            <p className="mt-1 text-xs text-[#64748b]">Aggiungi eventuali richieste speciali o istruzioni.</p>
            <textarea
              value={itemNotes}
              onChange={(e) => setItemNotes(e.target.value)}
              placeholder="Es: senza aglio, ingredienti particolare, etc."
              className="mt-3 w-full resize-none rounded-lg border border-[#e2e8f0] px-3 py-2 text-sm text-[#0f172a] placeholder:text-[#94a3b8] outline-none focus:border-[#cbd5e1] focus:ring-1 focus:ring-[#cbd5e1]"
              rows={3}
            />
          </section>
        )}

        {/* ── Extra info blocks ── ProductDetail.jsx style ──────────────────── */}
        {extraInfoBlocks.length ? (
          <section className="border-b border-[#e2e8f0] px-5 py-6">
            <p className="text-xs font-semibold uppercase tracking-[0.6px]" style={{ color: primaryColor }}>
              Informazioni prodotto
            </p>
            <div className="mt-4 space-y-4">
              {extraInfoBlocks.map((block) => {
                if (block.type === "gallery") {
                  return <GalleryCarousel key={block._key} block={block} primaryColor={primaryColor} />;
                }
                if (block.type === "text") {
                  return (
                    <div key={block._key} className="rounded-xl border border-[#e2e8f0] bg-white p-3">
                      <p className="text-sm leading-5 text-[#334155]">{block.content}</p>
                    </div>
                  );
                }
                if (block.type === "faq") {
                  return <FaqAccordion key={block._key} block={block} />;
                }
                // table
                return (
                  <div key={block._key}>
                    {block.title && (
                      <p className="mb-2 text-sm font-semibold text-[#0f172a]">{block.title}</p>
                    )}
                    <div className="overflow-x-auto rounded-xl border border-[#e2e8f0]">
                      <table className="min-w-full border-collapse text-left text-sm">
                        <thead>
                          <tr className="bg-[#f8fafc]">
                            {block.headers.map((header, i) => (
                              <th
                                key={`${block._key}-h-${i}`}
                                className="border-b border-[#e2e8f0] px-3 py-2 font-semibold text-[#334155]"
                              >
                                {header}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {block.rows.map((row, ri) => (
                            <tr key={`${block._key}-r-${ri}`}>
                              {block.headers.map((_, ci) => (
                                <td
                                  key={`${block._key}-c-${ri}-${ci}`}
                                  className="border-b border-[#e2e8f0] px-3 py-2 text-[#475569]"
                                >
                                  {row[ci] || "—"}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ) : null}
      </div>

      {/* ── Sticky footer — configurable products AND simple products ────── */}
      {(configurable || canAddSimple) && (
        <footer className="shrink-0 rounded-t-3xl bg-white px-5 pb-5 pt-4 shadow-[0_-3px_12px_rgba(0,0,0,0.08)]">
          {optionsAddonTotal !== 0 && (
            <div className="mb-2 flex items-center justify-between text-xs text-[#64748b]">
              <span>Extra opzioni</span>
              <span className="font-semibold text-[#334155]">{formatAddonLabel(optionsAddonTotal)}</span>
            </div>
          )}
          {missingRequiredCount > 0 && (
            <p className="mb-2 text-xs text-[#b45309]">
              Completa {missingRequiredCount} opzion{missingRequiredCount > 1 ? "i" : "e"}{" "}
              obbligatori{missingRequiredCount > 1 ? "e" : "a"}.
            </p>
          )}
          <button
            type="button"
            onClick={configurable
              ? handleSave
              : () => { onSetSimpleQty?.(product, quantity); onClose(); }
            }
            disabled={configurable ? !canSubmit : !canAddSimple}
            className="flex h-11 w-full items-center justify-between rounded-lg px-6 text-sm font-semibold text-white disabled:opacity-50"
            style={{ backgroundColor: primaryColor }}
          >
            <span>{editingLine ? "SALVA MODIFICHE" : "AGGIUNGI ALL'ORDINE"}</span>
            {totalPrice > 0 ? <span className="text-base font-bold">{formatCurrency(totalPrice)}</span> : null}
          </button>
        </footer>
      )}
    </div>
  );
}
